import { v4 as uuidv4 } from 'uuid';
import {
  csosUpmApi,
  IDeaInfo,
  IDeaNumUser,
  IError,
  IRequest,
  IRequestDetails,
  IRevokeRoles,
  parseDate,
  ResponseCSOSHeaders,
  searchEmailsMinLength,
} from '..';
import { pathOr } from 'rambda';
import { compose as lcompose, map as lmap } from 'lodash/fp';
import { Either, left, right } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/function';

export interface EmailOption {
  email: string;
  active?: boolean;
}
export interface SerialNumberOption {
  serialNumber: string;
  active?: boolean;
}

export const searchEmailsAdmin =
  (hR: ResponseCSOSHeaders) =>
    (email: string): Promise<EmailOption[]> =>
      email.length < searchEmailsMinLength
        ? new Promise(resolve => {
          resolve([]);
        })
        : csosUpmApi(hR)
          .post(`v1/api/admin/emails`, { email })
          .then(lcompose(pathOr([], 'data')));

export const searchSerialNumbersAdmin =
  (hR: ResponseCSOSHeaders) =>
    (serialNumber: string): Promise<SerialNumberOption[]> =>
      serialNumber.length < searchEmailsMinLength
        ? new Promise(resolve => {
          resolve([]);
        })
        : csosUpmApi(hR)
          .post(`v1/api/admin/serial-numbers`, { serialNumber })
          .then(lcompose(pathOr([], 'data')));

export const getUserRolesAdmin =
  (hR: ResponseCSOSHeaders) =>
    ({
      email,
      deaNumber,
      serialNumber,
    }: {
      email: string | null;
      deaNumber: string | null;
      serialNumber: string | null;
    }): Promise<Either<IError, IDeaNumUser[]>> =>
      csosUpmApi(hR)
        .post(`v1/api/admin/search`, {
          email,
          deaNumber,
          serialNumber,
        })
        .then(handleUserRolesSuccess)
        .catch(e => left(e as IError));

const handleUserRolesSuccess = lcompose(
  right,
  lmap(a => ({ ...a, key: uuidv4() })),
  pathOr<IDeaNumUser[]>([], 'data')
);

export const revokeRolesAdmin =
  (hR: ResponseCSOSHeaders) => async (input: IRevokeRoles) =>
    csosUpmApi(hR)
      .post(`v1/api/admin/revoke/roles`, input)
      .then(r => right(r.data))
      .catch(e => left(e));

export const searchRequest =
  (hR: ResponseCSOSHeaders) =>
    ({
      page,
      pageSize,
      sortBy,
      sortDirection,
      searchBy,
      searchP1,
      searchP2,
      di,
      requestorEmail,
      legacy
    }: {
      page: number;
      pageSize: number;
      sortBy: string;
      sortDirection: string;
      searchBy: string;
      searchP1: string;
      searchP2: string;
      di: boolean;
      requestorEmail: string;
      legacy?: boolean | null;
    }) =>
      csosUpmApi(hR)
        .get(
          legacy ? `v1/api/admin/legacy-request?${getsearchBy({
            searchBy,
            searchP1,
            searchP2,
          })}page=${page}&sizePerPage=${pageSize}&orderBy=${sortBy}&orderDirection=${sortDirection.toUpperCase()}${createdAt(
            { searchBy, searchP1, searchP2 }
          )}`
            : `v1/api/admin/request-search?${getsearchBy({
              searchBy,
              searchP1,
              searchP2,
            })}page=${page}&sizePerPage=${pageSize}&orderBy=${sortBy}&orderDirection=${sortDirection.toUpperCase()}${createdAt(
              { searchBy, searchP1, searchP2 }
            )}`
        )
        .then(flow(pathOr({ totalElements: 0, content: [] }, 'data'), right))
        .catch(e => left(e as IError));

const searchByMap = {
  requestId: 'requestId',
  status: 'requestStatus',
  reqType: 'reqType',
  requestorEmail: 'requestorEmail',
  sailpointCaseId: 'sailpointCaseId',
  sailpointRequestId: 'sailpointRequestId',
  deaNumbers: 'deaNumber',
};

const createdAt = ({
  searchBy,
  searchP1,
  searchP2,
}: {
  searchBy: string;
  searchP1: string;
  searchP2: string;
}) =>
  searchBy === 'createdAt'
    ? `&afterCreatedDate=${searchP1}&beforeCreatedDate=${searchP2}`
    : `&afterCreatedDate=2022-03-21&beforeCreatedDate=2032-07-21`;

const getsearchBy = ({
  searchBy,
  searchP1,
  searchP2,
}: {
  searchBy: string;
  searchP1: string;
  searchP2: string;
}) =>
  pathOr(null, searchBy)(searchByMap) === null
    ? ``
    : `${pathOr(null, searchBy)(searchByMap)}=${searchP1}&`;

export const getAdminRequests = (hR: ResponseCSOSHeaders) => {
  return csosUpmApi(hR)
    .get(`v1/api/admin/requests`)
    .then(
      lcompose(
        right,
        lmap<any, IRequest>(a => ({
          ...a,
          updatedAt: parseDate(a.updatedAt),
        })),
        pathOr([], 'data')
      )
    )
    .catch(e => left(e as IError));
};

const mapDeanum = (deaNum: string) => ({
  deaInfoId: 0,
  deaNum,
  msgCode: void 0,
  msgText: void 0,
});

// similar to getApproverRequestByRequestId
export const getAdminRequestByRequestId =
  (hR: ResponseCSOSHeaders) => (requestId: number) =>
    csosUpmApi(hR)
      .get(`v1/api/admin/requests/${requestId}`)
      .then(r =>
        right(
          flow(lmap<string, IDeaInfo>(mapDeanum), deaInfos => ({ deaInfos }))(
            r.data as string[]
          ) as unknown as IRequestDetails
        )
      )
      .catch(e => left(e as IError));
